import { ReactNode, useCallback, useMemo, useState } from 'react';

import { Button, Space, Tooltip, Typography } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsUserUserDetailModel, FanguardApiContractsUserUserSourceEnum } from 'src/api/generated';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useDeleteUser, useGetUsers, useResetUserPassword, useUnlockUser } from 'src/hooks/api/useUsersEndpoint';
import { useModal } from 'src/hooks/useModal';
import { usePagination } from 'src/hooks/usePagination';
import { useAppNavigation } from 'src/routes';

import { EditOutlined, LockOutlined } from '@ant-design/icons';

import UsersMobileRow from '@/pages/Users/components/UsersTable/components/UsersMobileRow';
import {
  ResetPasswordDescription,
  ResetPasswordWrapper,
  StyledDeleteOutlined,
  StyledSpace,
} from '@/pages/Users/components/UsersTable/UsersTable.styled';
import { StyledChangePassword } from '@/pages/Users/UsersContext.styled';
import { UsersContext } from '@/pages/Users/UsersContext';

const IS_ADMIN_USER = true;

export const AdminUsersProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { navigateTo } = useAppNavigation();
  const { confirm, contextHolder } = useModal();
  const { mutateAsync } = useDeleteUser();
  const { mutateAsync: resetUserPassword } = useResetUserPassword();
  const { mutateAsync: unlockUser } = useUnlockUser({ invalidateAll: true });
  const [activeUsername, setActiveUsername] = useState<string | null>(null);
  const [userResettingPassword, setUserResettingPassword] = useState<string | null>(null);
  const [userUnlocking, setUserUnlocking] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { page, pageSize, setPage, setPageSize } = usePagination();

  const { data: users, isLoading } = useGetUsers({
    page,
    pageSize,
    searchTerm,
  });

  const onPageChange = useCallback((pageNumber: number) => setPage(pageNumber), []);

  const filterBySearch = useCallback((value: string) => setSearchTerm(value), []);

  const onUnlockUser = async (d: FanguardApiContractsUserUserDetailModel) => {
    if (d.username) {
      setUserUnlocking(d.username);
      await unlockUser(d.username);
      setUserUnlocking(null);
    }
  };

  const onEditUser = (d: FanguardApiContractsUserUserDetailModel) => navigateTo.adminUsersDetails(d.username);

  const onDeleteUser = (d: FanguardApiContractsUserUserDetailModel) => {
    confirm({
      title: `${t('general.delete')} ${t('users.user')}?`,
      onOk: async () => {
        if (d.username) {
          await mutateAsync(d.username);
          navigateTo.adminUsers();
        }
      },
    });
  };

  const handleClickResetPassword = (d: FanguardApiContractsUserUserDetailModel) => {
    confirm({
      title: `${t('users.table.resetPassword')}?`,
      content: (
        <ResetPasswordWrapper>
          <ResetPasswordDescription $isDesktop>
            {t('users.table.resetPasswordTitle', { username: d.username, email: d.email })}
          </ResetPasswordDescription>
          <ResetPasswordDescription $isDesktop>
            {t('users.table.resetPasswordDescription', { username: d.username, email: d.email })}
          </ResetPasswordDescription>
        </ResetPasswordWrapper>
      ),
      onOk: async () => {
        await onResetUserPassword(d);
      },
    });
  };

  const onResetUserPassword = async (d: FanguardApiContractsUserUserDetailModel) => {
    if (d.username) {
      setUserResettingPassword(d.username);
      await resetUserPassword(d.username);
      setUserResettingPassword(null);
    }
  };

  const renderClients = (record: FanguardApiContractsUserUserDetailModel) => {
    const value = record.clients
      ?.map((c) => c.name ?? c.domainSlug)
      .filter(Boolean)
      .join(', ');
    return value ?? '—';
  };

  const desktopColumns: ColumnType<FanguardApiContractsUserUserDetailModel>[] = useMemo(
    () => [
      {
        title: t('users.table.firstName'),
        dataIndex: 'firstName',
        key: 'firstName',
        width: 132,
      },
      {
        title: t('users.table.lastName'),
        dataIndex: 'lastName',
        key: 'lastName',
        width: 132,
      },
      {
        title: t('users.table.emailAddress'),
        dataIndex: 'email',
        key: 'email',
        render: (value) => (
          <Tooltip title={value}>
            <Typography.Text ellipsis>{value}</Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: t('users.table.username'),
        dataIndex: 'username',
        key: 'username',
      },
      {
        title: t('users.table.clients'),
        dataIndex: 'clients',
        key: 'clients',
        render: (_value, record) => renderClients(record),
      },
      {
        title: t('users.table.actions'),
        dataIndex: 'actions',
        key: 'actions',
        width: 178,
        render: (_, record) => {
          const isInternalUser = record.source === FanguardApiContractsUserUserSourceEnum.Local;
          return (
            <StyledSpace size={8}>
              {isInternalUser && record.lockedOut ? (
                <Tooltip title={t('users.table.lockUser')}>
                  <Button
                    type="text"
                    icon={<LockOutlined />}
                    onClick={() => onUnlockUser(record)}
                    loading={userUnlocking === record.username}
                  />
                </Tooltip>
              ) : (
                <Space size={8} />
              )}
              {isInternalUser ? (
                <Tooltip title={t('users.table.resetPassword')}>
                  <Button
                    type="text"
                    icon={<StyledChangePassword />}
                    onClick={() => handleClickResetPassword(record)}
                    loading={userResettingPassword === record.username}
                  />
                </Tooltip>
              ) : null}
              <Tooltip title={t('users.table.editUser')}>
                <Button type="text" icon={<EditOutlined />} onClick={() => onEditUser(record)} />
              </Tooltip>
              <Tooltip title={t('users.table.deleteUser')}>
                <Button onClick={() => onDeleteUser(record)} type="text" icon={<StyledDeleteOutlined />} />
              </Tooltip>
            </StyledSpace>
          );
        },
      },
    ],
    [userResettingPassword, userUnlocking, t],
  );

  const mobileColumns: ColumnType<FanguardApiContractsUserUserDetailModel>[] = useMemo(
    () => [
      {
        title: t('users.table.firstName'),
        dataIndex: 'firstName',
        key: 'firstName',
        render: (_value, record) => (
          <UsersMobileRow
            users={users?.items ?? []}
            user={record}
            activeUsername={activeUsername}
            setActiveUsername={setActiveUsername}
            onEditUser={(username) => navigateTo.adminUsersDetails(username)}
            onAfterDelete={() => navigateTo.adminUsers()}
            showResetPassword={record.source === FanguardApiContractsUserUserSourceEnum.Local}
            isAdminUser={IS_ADMIN_USER}
          />
        ),
      },
    ],
    [users, activeUsername, setActiveUsername, t, navigateTo],
  );

  const tableColumns = isDesktop ? desktopColumns : mobileColumns;

  const contextValue = useMemo(
    () => ({
      isLoading: isLoading,
      users: users?.items ?? [],
      searchTerm,
      tableColumns,
      page,
      pageSize,
      setPageSize,
      totalItems: users?.totalItems ?? 0,
      filterBySearch,
      onPageChange,
    }),
    [isLoading, users, filterBySearch, searchTerm, tableColumns, page, pageSize, setPageSize],
  );

  return (
    <UsersContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </UsersContext.Provider>
  );
};
